/**
 * The arithmetic behind the composed course thumbnail (`CourseCover`) and the
 * reviewer's photo crop (`ThumbnailSheet`).
 *
 * A coach photo is never cut: the server stores the whole photo and a crop —
 * the point of the photo at the centre of the round frame (`x`, `y`, as
 * fractions of its width and height) and a zoom over the smallest scale that
 * fills the frame. These turn that into pixels, and a drag back into a crop.
 */

export type Crop = {x: number; y: number; zoom: number};

/** The composition is laid out at this size and scaled to fit. */
export const COVER_W = 640;
export const COVER_H = 360;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/**
 * Size and offset of the photo inside a round frame `size` px across, so that
 * the crop point sits at the centre — moved only as far as needed to keep the
 * frame covered. Unknown photo size: a plain fill.
 */
export function photoLayout(
  size: number,
  photoWidth: number,
  photoHeight: number,
  crop: Crop,
): {width: number; height: number; left: number; top: number} {
  if (!photoWidth || !photoHeight) return {width: size, height: size, left: 0, top: 0};
  const scale = (size / Math.min(photoWidth, photoHeight)) * clamp(crop.zoom || 1, 1, 4);
  const width = photoWidth * scale;
  const height = photoHeight * scale;
  const left = clamp(size / 2 - clamp(crop.x, 0, 1) * width, size - width, 0);
  const top = clamp(size / 2 - clamp(crop.y, 0, 1) * height, size - height, 0);
  return {width, height, left, top};
}

/**
 * The crop after dragging the photo by (dx, dy) px in a frame `size` px
 * across. Dragging the photo down shows more of its top, so the centre point
 * moves up; it stops where the photo would leave the frame.
 */
export function dragCrop(
  crop: Crop,
  dx: number,
  dy: number,
  size: number,
  photoWidth: number,
  photoHeight: number,
): Crop {
  const {width, height} = photoLayout(size, photoWidth, photoHeight, crop);
  const halfX = size / 2 / width;
  const halfY = size / 2 / height;
  return {
    ...crop,
    x: clamp(crop.x - dx / width, halfX, 1 - halfX),
    y: clamp(crop.y - dy / height, halfY, 1 - halfY),
  };
}

export function initials(name: string): string {
  const letters = (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0].toUpperCase())
    .join('');
  return letters || '?';
}

export function coachLabel(coach: {title: string; name: string}): string {
  return [coach.title, coach.name].filter(Boolean).join(' ');
}

export function sideToMove(fen?: string | null): 'white' | 'black' {
  return (fen || '').trim().split(/\s+/)[1] === 'b' ? 'black' : 'white';
}
